import React from 'react';
import { Box, Typography } from '@mui/material';
import OntologyBrowser, {
    CodeText, OntologyColumn, ScopeLevelChip, SCOPE_LEVEL_FILTER, StatusChip, STATUS_FILTER,
} from './OntologyBrowser';
import { dsVar } from '../../theme/tokens';

const columns: OntologyColumn[] = [
    {
        key: 'title',
        label: 'Policy',
        width: '36%',
        render: (doc) => (
            <Box>
                <Typography component="div" sx={{ fontSize: '0.875rem', color: dsVar('fg-body'), fontWeight: 500 }}>
                    {doc.title || doc.policy_code}
                    {doc.section && (
                        <Typography component="span" sx={{ ml: 0.75, fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                            {doc.section}
                        </Typography>
                    )}
                </Typography>
                <CodeText>{doc.policy_code}</CodeText>
            </Box>
        ),
    },
    {
        key: 'narrative',
        label: 'What it says',
        width: '30%',
        render: (doc) => (
            <Typography
                component="div"
                sx={{
                    fontSize: '0.8125rem', color: dsVar('fg-2'), lineHeight: 1.55,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {doc.narrative || '—'}
            </Typography>
        ),
    },
    {
        key: 'governs',
        label: 'Governs',
        render: (doc) => {
            const count = Array.isArray(doc.governs_concepts) ? doc.governs_concepts.length : 0;
            return (
                <Typography component="span" sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                    {count === 0 ? '—' : `${count} concept${count === 1 ? '' : 's'}`}
                </Typography>
            );
        },
    },
    {
        key: 'owner_role',
        label: 'Owner',
        // Who should be reviewing this policy's proposed thresholds.
        render: (doc) => (
            <Typography component="span" sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                {doc.owner_role || '—'}
            </Typography>
        ),
    },
    {
        key: 'scope_level',
        label: 'Scope',
        render: (doc) => <ScopeLevelChip scopeLevel={doc.scope_level} />,
    },
    {
        key: 'status',
        label: 'Status',
        render: (doc) => <StatusChip status={doc.status} />,
    },
];

interface PoliciesBrowserProps {
    filters: Record<string, string>;
    onRowSelect?: (doc: Record<string, any>) => void;
    selectedCode?: string | null;
}

const PoliciesBrowser: React.FC<PoliciesBrowserProps> = ({
    filters, onRowSelect, selectedCode,
}) => (
    <OntologyBrowser
        kind="policies"
        filters={filters}
        columns={columns}
        filterControls={[SCOPE_LEVEL_FILTER, STATUS_FILTER]}
        searchPlaceholder="Search titles, codes and narratives"
        emptyMessage="No policies match these filters."
        onRowSelect={onRowSelect}
        selectedCode={selectedCode}
    />
);

export default PoliciesBrowser;
